// Objective sounds driven by the mode snapshot: the planted bomb's beeps (paced by the time left),
// the ticks of a plant or defuse hold (flat for the local player, positional for everyone else, so
// the other team hears an attempt) and the alarm when a hill becomes contested.

import type { AudioEngine } from "../audio";
import { TICK_RATE } from "../net/constants";
import type { ModeSnapData, Vec3Like } from "../wasm/types";
import { BombBeeper } from "./audioCues";

type Sink = Pick<AudioEngine, "playBombBeep" | "playBombAction" | "playUi">;

/** Time between the ticks of a plant or defuse hold (ms). */
export const ACTION_TICK_MS = 450;
/** A hill flipping in and out of contest sounds the alarm at most this often (ms). */
export const CONTEST_COOLDOWN_MS = 4000;

export class ObjectiveAudio {
  private mode: ModeSnapData | null = null;
  private readonly beeper = new BombBeeper();
  private nextAction = 0;
  private contested = false;
  private lastContest = Number.NEGATIVE_INFINITY;

  constructor(private readonly audio: Sink) {}

  onSnapshot(mode: ModeSnapData): void {
    this.mode = mode;
  }

  clear(): void {
    this.mode = null;
    this.beeper.reset();
    this.nextAction = 0;
    this.contested = false;
    this.lastContest = Number.NEGATIVE_INFINITY;
  }

  /** Every frame. `positionOf` gives where another player stands (null when unknown). */
  frame(nowMs: number, nowTick: number, myId: number, positionOf: (id: number) => Vec3Like | null): void {
    const mode = this.mode;
    if (!mode) return;
    const hill = mode.hill;
    const contested = hill !== null && hill.active && hill.contested;
    if (contested && !this.contested && nowMs - this.lastContest >= CONTEST_COOLDOWN_MS) {
      this.lastContest = nowMs;
      this.audio.playUi("hillContest");
    }
    this.contested = contested;

    const bomb = mode.bomb;
    const state = bomb?.state;
    if (state?.kind === "planted") {
      const urgency = this.beeper.poll(nowMs, (state.explodeTick - nowTick) / TICK_RATE);
      if (urgency !== null) this.audio.playBombBeep(state.pos, urgency);
    } else {
      this.beeper.reset();
    }

    if (bomb && bomb.actor !== null && (state?.kind === "carried" || state?.kind === "planted")) {
      if (nowMs < this.nextAction) return;
      this.nextAction = nowMs + ACTION_TICK_MS;
      const action = state.kind === "planted" ? "defuse" : "plant";
      const progress = bomb.progress / 255;
      if (bomb.actor === myId) {
        this.audio.playBombAction(action, progress);
        return;
      }
      const pos = positionOf(bomb.actor) ?? (state.kind === "planted" ? state.pos : null);
      if (pos) this.audio.playBombAction(action, progress, pos);
    } else {
      this.nextAction = 0;
    }
  }
}
